'use client'
import Image from 'next/image'
import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { Menu, X, Globe, Eye, Calendar, ChevronDown, Check, Phone, Mail } from 'lucide-react'
import { useLanguage } from '@/lib/language-context'
import { useAccessibility } from '@/lib/accessibility-context'
import { Language } from '@/lib/i18n'

const LANGUAGES: { code: Language; label: string; native: string }[] = [
  { code: 'en', label: 'English', native: 'EN' },
  { code: 'hi', label: 'हिंदी', native: 'HI' },
  { code: 'mr', label: 'मराठी', native: 'MR' },
]

export function PublicHeader() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const { language, setLanguage, t } = useLanguage()
  const { setIsOpen: setAccessOpen } = useAccessibility()
  const [eventsOpen, setEventsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const navLinks = [
    { labelKey: 'navHome' as const, href: '/' },
    { labelKey: 'navAbout' as const, href: '/about' },
    { labelKey: 'navComplaints' as const, href: '/raise-complaint' },
    { labelKey: 'navRegistration' as const, href: '/registration' },
    { labelKey: 'navFeedback' as const, href: '/feedback' }
  ];

  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0]

  return (
    <>
      {/* Top utility bar */}
      <div style={{ background: '#1a0d04', borderBottom: '1px solid rgba(243,156,18,0.2)' }} className="py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
          <span className="ml-auto">{t('helpline')}</span>
        </div>
      </div>

      {/* Main header */}
      <header
        className="sticky top-0 z-50 transition-all duration-300"
        style={{
          background: scrolled
            ? 'rgba(32,17,7,0.95)'
            : 'rgba(32,17,7,0.88)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(243,156,18,0.2)',
          boxShadow: scrolled ? '0 8px 32px rgba(0,0,0,0.4)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 group flex-shrink-0">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center shadow-lg overflow-hidden transition-all duration-300 group-hover:scale-105"
              style={{ border: '2px solid rgba(243,156,18,0.5)', background: 'white' }}
            >
              <Image src="/logo.jpeg" alt="Pune Pratham Logo" width={44} height={44} className="object-cover w-full h-full" />
            </div>
            <div>
              <div
                className="font-bold text-lg tracking-wide transition-colors"
                style={{ fontFamily: 'Poppins, sans-serif', color: '#F39C12' }}
              >
                {t('brandName')}
              </div>
              <div className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>{t('brandSubtitle')}</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1 flex-1 justify-end">
            {navLinks.map(link => (
              <Link
                key={link.href}
                href={link.href}
                className="relative text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 group"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                <span className="relative z-10">{t(link.labelKey)}</span>
                <span
                  className="absolute bottom-0 left-4 right-4 h-0.5 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
                  style={{ background: '#F39C12' }}
                />
              </Link>
            ))}

            {/* Events Dropdown */}
            <div className="relative">
              <button
                onClick={() => { setEventsOpen(!eventsOpen); setLangOpen(false) }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg transition-all text-sm font-medium group"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                <Calendar className="w-4 h-4" />
                <span>Events</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${eventsOpen ? 'rotate-180' : ''}`} />
                <span
                  className="absolute bottom-0 left-4 right-4 h-0.5 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
                  style={{ background: '#F39C12' }}
                />
              </button>

              {eventsOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setEventsOpen(false)} />
                  <div
                    className="absolute right-0 top-full mt-2 w-48 rounded-xl shadow-2xl z-50 overflow-hidden"
                    style={{
                      background: 'rgba(30,15,5,0.95)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(243,156,18,0.25)',
                    }}
                  >
                    <Link
                      href="/events"
                      className="flex items-center gap-3 px-4 py-3 transition-all border-b"
                      style={{ borderColor: 'rgba(243,156,18,0.15)', color: 'rgba(255,255,255,0.85)' }}
                      onClick={() => setEventsOpen(false)}
                    >
                      <Calendar className="w-4 h-4" style={{ color: '#F39C12' }} />
                      <span className="text-sm font-medium">Events</span>
                    </Link>
                    <Link
                      href="/surveys"
                      className="flex items-center gap-3 px-4 py-3 transition-all"
                      style={{ color: 'rgba(255,255,255,0.85)' }}
                      onClick={() => setEventsOpen(false)}
                    >
                      <Check className="w-4 h-4" style={{ color: '#F39C12' }} />
                      <span className="text-sm font-medium">Surveys</span>
                    </Link>
                  </div>
                </>
              )}
            </div>

            {/* Language Switcher */}
            <div className="relative ml-1">
              <button
                onClick={() => { setLangOpen(!langOpen); setEventsOpen(false) }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all text-sm font-medium"
                style={{ color: 'rgba(255,255,255,0.85)' }}
                aria-label="Change language"
              >
                <Globe className="w-4 h-4" />
                <span>{currentLang.native}</span>
              </button>

              {langOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
                  <div
                    className="absolute right-0 top-full mt-2 rounded-xl shadow-2xl z-50 overflow-hidden min-w-[140px]"
                    style={{
                      background: 'rgba(30,15,5,0.95)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(243,156,18,0.25)',
                    }}
                  >
                    {LANGUAGES.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => { setLanguage(lang.code); setLangOpen(false) }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all ${
                          language === lang.code ? 'font-semibold' : ''
                        }`}
                        style={{
                          color: language === lang.code ? '#F39C12' : 'rgba(255,255,255,0.8)',
                          background: language === lang.code ? 'rgba(243,156,18,0.1)' : 'transparent',
                        }}
                      >
                        <span className="font-mono text-xs font-bold w-6 text-center">{lang.native}</span>
                        <span>{lang.label}</span>
                        {language === lang.code && <span className="ml-auto" style={{ color: '#F39C12' }}>✓</span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Accessibility Button */}
            <button
              onClick={() => setAccessOpen(true)}
              title={`${t('accessibility')} (Ctrl+F2)`}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all text-sm font-medium ml-1"
              style={{ color: 'rgba(255,255,255,0.85)' }}
            >
              <Eye className="w-4 h-4" />
              <span className="hidden lg:inline">{t('accessibility')}</span>
            </button>

            <Link
              href="/login"
              className="ml-2 px-5 py-2 rounded-xl font-semibold text-sm transition-all hover:scale-105 shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #F39C12, #E67E22)',
                color: 'white',
                boxShadow: '0 4px 16px rgba(230,126,34,0.4)',
              }}
            >
              {t('navAdminLogin')}
            </Link>
          </nav>

          {/* Mobile right controls */}
          <div className="flex md:hidden items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="text-white p-2 rounded-lg"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                <Globe className="w-5 h-5" />
              </button>
              {langOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
                  <div
                    className="absolute right-0 top-full mt-2 rounded-xl shadow-2xl z-50 overflow-hidden min-w-[140px]"
                    style={{
                      background: 'rgba(30,15,5,0.95)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(243,156,18,0.25)',
                    }}
                  >
                    {LANGUAGES.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => { setLanguage(lang.code); setLangOpen(false) }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm"
                        style={{
                          color: language === lang.code ? '#F39C12' : 'rgba(255,255,255,0.8)',
                          background: language === lang.code ? 'rgba(243,156,18,0.1)' : 'transparent',
                        }}
                      >
                        <span className="font-mono text-xs font-bold w-6 text-center">{lang.native}</span>
                        <span>{lang.label}</span>
                        {language === lang.code && <span className="ml-auto" style={{ color: '#F39C12' }}>✓</span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => setAccessOpen(true)}
              className="p-2 rounded-lg"
              style={{ color: 'rgba(255,255,255,0.85)' }}
            >
              <Eye className="w-5 h-5" />
            </button>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 rounded-lg"
              style={{ color: 'rgba(255,255,255,0.85)' }}
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            className="md:hidden px-4 pb-4 pt-2"
            style={{ borderTop: '1px solid rgba(243,156,18,0.15)' }}
          >
            {navLinks.map(link => (
              <Link
                key={link.href}
                href={link.href}
                className="block py-3 text-sm font-medium transition-colors"
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  borderBottom: '1px solid rgba(255,255,255,0.07)',
                }}
                onClick={() => setMobileOpen(false)}
              >
                {t(link.labelKey)}
              </Link>
            ))}
            <Link
              href="/events"
              className="block py-3 text-sm font-medium transition-colors"
              style={{
                color: 'rgba(255,255,255,0.85)',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
              }}
              onClick={() => setMobileOpen(false)}
            >
              Events
            </Link>

            <Link
              href="/login"
              className="block mt-4 text-center py-3 rounded-xl font-semibold text-sm"
              style={{
                background: 'linear-gradient(135deg, #F39C12, #E67E22)',
                color: 'white',
              }}
              onClick={() => setMobileOpen(false)}
            >
              {t('navAdminLogin')}
            </Link>
          </div>
        )}
      </header>
    </>
  )
}
